/**
 * Legal entities — the registered companies in the group.
 *
 * A legal entity is what a journal entry, a fiscal year and a chart of
 * accounts all hang from, so this is the first screen an implementation
 * touches after provisioning.
 *
 * The form picks nothing for the person: the country is empty until chosen,
 * and the currency starts as the workspace currency (D108, CUR-2).
 *
 * Arabic and English (C7): every word from the catalogs; a country is named
 * in the interface's language, and an API refusal is shown through ErrorText.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { post, useApiList, useApiMutation } from "@/lib/api/hooks";
import { currencyPickerOptions, useWorkspaceCurrency } from "@/lib/currency";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { countryName, useCountryOptions } from "@/lib/reference";
import type { LegalEntityRow } from "./types";

export function LegalEntitiesPage() {
  const { data, isLoading, error } = useApiList<LegalEntityRow>(
    ["org", "legal-entities"],
    "/legal-entities",
  );
  const { t, i18n } = useTranslation();
  const { options: countries } = useCountryOptions();
  const workspaceCurrency = useWorkspaceCurrency();
  const currencies = currencyPickerOptions(i18n.language, workspaceCurrency);
  const [creating, setCreating] = useState(false);
  const blank = () => ({
    name: "",
    legalName: "",
    registrationCountry: "",
    currencyCode: workspaceCurrency ?? "",
    taxId: "",
  });
  const [form, setForm] = useState(blank);

  const create = useApiMutation<typeof form, unknown>(
    ["org", "legal-entities"],
    (input) =>
      post("/legal-entities", {
        name: input.name,
        legalName: input.legalName || input.name,
        registrationCountry: input.registrationCountry,
        currencyCode: input.currencyCode,
        taxId: input.taxId || undefined,
      }),
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    create.mutate(form, {
      onSuccess: () => {
        setCreating(false);
        setForm(blank());
      },
    });
  };

  const title = t("org.legalEntities.title");

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={t("org.legalEntities.subtitle")}
        breadcrumb={[t("org.section"), title]}
        actions={
          <Button
            variant="primary"
            onClick={() => {
              if (!creating) setForm(blank());
              setCreating((open) => !open);
            }}
          >
            {creating ? t("common.cancel") : t("org.legalEntities.new")}
          </Button>
        }
      />

      {creating && (
        <Card title={t("org.legalEntities.new")} className="mb-4">
          <form onSubmit={submit}>
            <div className="atlas-grid atlas-grid--2">
              <Field
                label={t("fields.name")}
                required
                hint={t("org.legalEntities.nameHint")}
              >
                <Input
                  value={form.name}
                  required
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Field>
              <Field
                label={t("org.legalEntities.legalName")}
                hint={t("org.legalEntities.legalNameHint")}
              >
                <Input
                  value={form.legalName}
                  onChange={(e) =>
                    setForm({ ...form, legalName: e.target.value })
                  }
                />
              </Field>
              <Field label={t("org.legalEntities.country")} required>
                <Select
                  value={form.registrationCountry}
                  required
                  onChange={(e) =>
                    setForm({ ...form, registrationCountry: e.target.value })
                  }
                >
                  <option value="">{t("common.chooseCountry")}</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label={t("org.legalEntities.currency")}
                required
                hint={t("org.legalEntities.currencyHint")}
              >
                <Select
                  value={form.currencyCode}
                  required
                  onChange={(e) =>
                    setForm({ ...form, currencyCode: e.target.value })
                  }
                >
                  {!form.currencyCode && (
                    <option value="">{t("money.chooseCurrency")}</option>
                  )}
                  {currencies.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t("org.legalEntities.taxId")}>
                <Input
                  value={form.taxId}
                  onChange={(e) => setForm({ ...form, taxId: e.target.value })}
                />
              </Field>
            </div>
            {create.error && (
              <div
                role="alert"
                style={{
                  color: "var(--atlas-danger)",
                  fontSize: 13,
                  marginBlock: 8,
                }}
              >
                <ErrorText error={create.error} />
              </div>
            )}
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending
                ? t("org.legalEntities.creating")
                : t("org.legalEntities.create")}
            </Button>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Skeleton rows={8} />
      ) : error ? (
        <EmptyState
          title={t("org.legalEntities.loadFailed")}
          action={
            <div role="alert" style={{ fontSize: 14 }}>
              <ErrorText error={error} />
            </div>
          }
        />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title={t("org.legalEntities.emptyTitle")}
          message={t("org.legalEntities.emptyMessage")}
          action={
            <Button
              variant="primary"
              onClick={() => {
                setForm(blank());
                setCreating(true);
              }}
            >
              {t("org.legalEntities.new")}
            </Button>
          }
        />
      ) : (
        <Card title={listTitle(title, data.total)}>
          <DataTable<LegalEntityRow>
            rows={data.items}
            columns={[
              {
                key: "name",
                label: t("fields.name"),
                render: (r) => (
                  <Link to={`/app/organization/legal-entities/${r.id}`}>
                    {r.name}
                  </Link>
                ),
              },
              {
                key: "legal_name",
                label: t("org.legalEntities.registeredName"),
              },
              {
                key: "registration_country",
                label: t("fields.country"),
                render: (r) =>
                  countryName(r.registration_country, i18n.language),
              },
              { key: "currency_code", label: t("fields.currency") },
              {
                key: "tax_id",
                label: t("fields.taxId"),
                render: (r) => r.tax_id ?? "—",
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="LegalEntity" />
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
